import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid'
import { cn } from 'app/lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { logError } from 'lib/logging'

export interface BaseAudioPlayerProps {
  id?: string
  activePlayerId?: string
  url?: string
  className?: string
  disabled?: boolean
  onPlay?: (id: string) => void
}

const formatTime = (seconds: number | null) => {
  if (seconds === null || !isFinite(seconds) || seconds < 0) return `--:--`
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, `0`)}:${secs.toString().padStart(2, `0`)}`
}

export const BaseAudioPlayer = ({ id, activePlayerId, url, className, disabled, onPlay }: BaseAudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState<number | null>(null)

  const isDisabled = disabled || !url

  const togglePlay = useCallback(async () => {
    if (isDisabled || !audioRef.current) return
    if (audioRef.current.paused) {
      if (id) onPlay?.(id)
      await audioRef.current.play()
    } else {
      audioRef.current.pause()
    }
  }, [isDisabled, id, onPlay])

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDisabled || !audioRef.current || !progressRef.current) return
      const rect = progressRef.current.getBoundingClientRect()
      const ratio = (e.clientX - rect.left) / rect.width
      audioRef.current.currentTime = ratio * audioRef.current.duration
    },
    [isDisabled],
  )

  // Pause when another player becomes active
  useEffect(() => {
    if (activePlayerId && activePlayerId !== id && isPlaying && audioRef.current) {
      audioRef.current.pause()
    }
  }, [activePlayerId, id, isPlaying])

  useEffect(() => {
    if (!url) {
      setIsPlaying(false)
      setCurrentTime(0)
      setDuration(null)
    }
  }, [url])

  const progress = duration && duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className={cn(`flex items-center gap-3`, className)}>
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={() => {
          if (audioRef.current) setDuration(audioRef.current.duration)
        }}
        onTimeUpdate={() => {
          if (audioRef.current) setCurrentTime(audioRef.current.currentTime)
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false)
          setCurrentTime(0)
        }}
        onError={() => {
          logError(`audio-player`, `playback error`, { url })
          setIsPlaying(false)
        }}
      />

      <button
        type="button"
        className={cn(
          `flex h-7 w-7 shrink-0 items-center justify-center rounded-full`,
          isDisabled ? `pointer-events-none bg-surface-strong` : `cursor-pointer bg-blue-600`,
        )}
        disabled={isDisabled}
        onClick={togglePlay}
        aria-label={isPlaying ? `Pause` : `Play`}
      >
        {isPlaying ? (
          <PauseIcon className="h-3.5 w-3.5 text-on-color" />
        ) : (
          <PlayIcon className="h-3.5 w-3.5 text-on-color" />
        )}
      </button>

      <div
        ref={progressRef}
        className={cn(
          `relative h-1.5 flex-1 rounded-full bg-surface-strong`,
          isDisabled ? `pointer-events-none` : `cursor-pointer`,
        )}
        onClick={handleSeek}
      >
        <div
          className="absolute left-0 top-0 h-full rounded-full bg-blue-600 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      <span className="shrink-0 text-xs text-subtle">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>
    </div>
  )
}
